import { useState, useEffect, useRef, useCallback, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { CarouselEvent, Member } from '../../types';
import { format } from 'date-fns';
import exifr from 'exifr';
import { useFaceDetection, preloadFaceModels } from '../../hooks/useFaceDetection';
import { parseEventDate } from '../../utils/eventDate';
import { FaceTagRow, type FaceTagRowFace } from '../../components/FaceTagRow';
import { UNSUPPORTED_PREVIEW_DATA_URL, extractPhotoMetadata, type PhotoMetadata } from '../../utils/extractPhotoMetadata';
import { TemplatePicker } from '../../components/TemplatePicker';
import { getEventTemplate, DEFAULT_TEMPLATE_KIND, type TemplateKind } from '../../utils/eventTemplates';
import { generatePhotoCaption } from '../../utils/generatePhotoCaption';
import { AudioRecorder, audioFileName } from '../../components/AudioRecorder';
import { PHOTO_ACCEPT, serverErrorMessage, describeUploadFailures } from '../../utils/photoUpload';

type StagedFace = FaceTagRowFace;

interface StagedAudio {
  blob: Blob;
  url: string;
  durationSec: number;
  label: string;
}

export function EventForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [location, setLocation] = useState('');
  const [templateKind, setTemplateKind] = useState<TemplateKind>(DEFAULT_TEMPLATE_KIND);
  const [members, setMembers] = useState<Member[]>([]);

  const [participants, setParticipants] = useState<string[]>([]);

  const [stagedPhotos, setStagedPhotos] = useState<File[]>([]);
  const [stagedPreviews, setStagedPreviews] = useState<string[]>([]);
  const [stagedTags, setStagedTags] = useState<string[][]>([]);
  const [stagedFaces, setStagedFaces] = useState<StagedFace[][]>([]);
  const [stagedMeta, setStagedMeta] = useState<(PhotoMetadata | null)[]>([]);
  const [stagedCaptions, setStagedCaptions] = useState<string[]>([]);
  const [stagedCaptionsEdited, setStagedCaptionsEdited] = useState<boolean[]>([]);
  const [detectingFaces, setDetectingFaces] = useState(false);
  const [stagedAudio, setStagedAudio] = useState<StagedAudio[]>([]);
  const [recorderBusy, setRecorderBusy] = useState(false);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [stagedParticipants, setStagedParticipants] = useState<string[]>([]);

  const [membersError, setMembersError] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRelationship, setNewMemberRelationship] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberError, setAddMemberError] = useState('');

  const { detectFaces } = useFaceDetection();

  useEffect(() => {
    // Only the create form shows a photo picker (see the !isEdit block below) —
    // start the face-detection model download now, well before the user picks
    // a photo, so it's already warm by the time detectFaces() runs.
    if (!isEdit) preloadFaceModels();
    api.get('/account/members')
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembersError(true));
    if (isEdit && id) {
      api.get(`/events/${id}`).then(({ data }: { data: CarouselEvent }) => {
        setTitle(data.title);
        setDescription(data.description || '');
        setEventDate(data.eventDate ? format(parseEventDate(data.eventDate), 'yyyy-MM-dd') : '');
        setLocation(data.location || '');
        setTemplateKind(getEventTemplate(data.templateKind).kind);
        setParticipants(data.participants.map(p => p.userId));
      });
    }
  }, [id, isEdit]);

  // Recompute suggested captions for every staged photo the user hasn't
  // manually edited yet, whenever the signals feeding them change (template,
  // location, per-photo EXIF metadata as it resolves, or face tags).
  useEffect(() => {
    setStagedCaptions(prev => stagedMeta.map((meta, i) => {
      if (stagedCaptionsEdited[i]) return prev[i] ?? '';
      const names = (stagedTags[i] ?? [])
        .map(uid => members.find(m => m.user.id === uid)?.user.name)
        .filter((n): n is string => Boolean(n));
      return generatePhotoCaption({
        templateKind,
        takenAt: meta?.takenAt ?? null,
        location: location || null,
        participantNames: names,
      });
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKind, location, stagedMeta, stagedTags, members]);

  async function handleAddMember() {
    if (!newMemberName.trim()) return;
    setAddMemberLoading(true);
    setAddMemberError('');
    try {
      const { data } = await api.post('/account/members', {
        name: newMemberName.trim(),
        relationship: newMemberRelationship.trim() || undefined,
      });
      setMembers(prev => [...prev, data]);
      if (isEdit && id) {
        await api.post(`/events/${id}/participants`, { userId: data.user.id });
        setParticipants(prev => [...prev, data.user.id]);
      } else {
        setStagedParticipants(prev => [...prev, data.user.id]);
      }
      setNewMemberName('');
      setNewMemberRelationship('');
      setShowAddMember(false);
    } catch (err: unknown) {
      setAddMemberError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to add person');
    } finally {
      setAddMemberLoading(false);
    }
  }

  // ── Face assignment ────────────────────────────────────────────────────────

  const assignFace = useCallback((photoIdx: number, faceIdx: number, userId: string | null) => {
    setStagedFaces(prev => {
      const oldUserId = prev[photoIdx]?.[faceIdx]?.assignedUserId ?? null;

      const nextFaces = prev.map((faces, pi) =>
        pi !== photoIdx ? faces :
        faces.map((f, fi) => fi !== faceIdx ? f : { ...f, assignedUserId: userId })
      );

      setStagedTags(tags => tags.map((t, pi) => {
        if (pi !== photoIdx) return t;
        let next = [...t];
        if (oldUserId && oldUserId !== userId) {
          const otherFaceHas = (prev[pi] ?? []).some((f, fi) => fi !== faceIdx && f.assignedUserId === oldUserId);
          if (!otherFaceHas) next = next.filter(u => u !== oldUserId);
        }
        if (userId && !next.includes(userId)) next = [...next, userId];
        return next;
      }));

      return nextFaces;
    });
  }, []);

  async function handleAddPersonForFace(photoIdx: number, faceIdx: number, name: string) {
    const { data } = await api.post('/account/members', { name });
    setMembers(prev => [...prev, data]);
    assignFace(photoIdx, faceIdx, data.user.id);
  }

  // ── Staged photo handlers ──────────────────────────────────────────────────

  async function handlePhotoSelect(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const newUrls = files.map(f => URL.createObjectURL(f));
    const photoOffset = stagedPhotos.length;

    setStagedPhotos(prev => [...prev, ...files]);
    setStagedPreviews(prev => [...prev, ...newUrls]);
    setStagedTags(prev => [...prev, ...files.map(() => [])]);
    setStagedFaces(prev => [...prev, ...files.map(() => [])]);
    setStagedMeta(prev => [...prev, ...files.map(() => null)]);
    setStagedCaptions(prev => [...prev, ...files.map(() => '')]);
    setStagedCaptionsEdited(prev => [...prev, ...files.map(() => false)]);

    // Per-photo metadata (EXIF date/GPS) for caption suggestions — independent
    // of the files[0]-only autofill below, which stays untouched so existing
    // location-autofill behavior doesn't change.
    (async () => {
      for (let i = 0; i < files.length; i++) {
        const photoIdx = photoOffset + i;
        try {
          const meta = await extractPhotoMetadata(files[i]);
          setStagedMeta(prev => prev.map((m, idx) => idx === photoIdx ? meta : m));
        } catch { /* leave as null — caption falls back to no date signal */ }
      }
    })();

    // EXIF date from first photo
    try {
      const meta = await exifr.parse(files[0], ['DateTimeOriginal']);
      if (meta?.DateTimeOriginal) {
        setEventDate(prev => prev || format(new Date(meta.DateTimeOriginal), 'yyyy-MM-dd'));
      }
    } catch { /* no date EXIF */ }

    // EXIF GPS → reverse geocode from first photo
    try {
      const gps = await exifr.gps(files[0]);
      if (gps?.latitude && gps?.longitude) {
        const resp = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${gps.latitude}&lon=${gps.longitude}&zoom=10`,
          { headers: { 'Accept': 'application/json', 'User-Agent': 'EventsCarousel/1.0 (family memory app)' } }
        );
        if (resp.ok) {
          const data = await resp.json();
          const addr = data.address ?? {};
          const city = addr.city ?? addr.town ?? addr.village ?? addr.county ?? '';
          const country = addr.country ?? '';
          const place = [city, country].filter(Boolean).join(', ');
          if (place) setLocation(prev => prev || place);
        }
      }
    } catch { /* no GPS EXIF */ }

    // Face detection for all new photos — run one at a time, not concurrently.
    // Each detectFaces() call runs a real ML model (face-api.js) on the main
    // thread; firing them all at once via Promise.all floods the JS thread and
    // GPU context simultaneously and freezes the UI for several seconds on a
    // multi-photo upload. Sequential processing spaces the work out (each
    // photo's faces appear as soon as they're ready) and keeps the app responsive.
    setDetectingFaces(true);
    (async () => {
      for (let i = 0; i < newUrls.length; i++) {
        const photoIdx = photoOffset + i;
        const faces = await detectFaces(newUrls[i]);

        const staged: StagedFace[] = faces.map(f => ({ cropUrl: f.cropUrl, assignedUserId: null }));
        setStagedFaces(prev => prev.map((pf, idx) => idx === photoIdx ? staged : pf));
      }
    })().finally(() => setDetectingFaces(false));

    e.target.value = '';
  }

  function removeStagedPhoto(index: number) {
    URL.revokeObjectURL(stagedPreviews[index]);
    setStagedPhotos(prev => prev.filter((_, i) => i !== index));
    setStagedPreviews(prev => prev.filter((_, i) => i !== index));
    setStagedTags(prev => prev.filter((_, i) => i !== index));
    setStagedFaces(prev => prev.filter((_, i) => i !== index));
    setStagedMeta(prev => prev.filter((_, i) => i !== index));
    setStagedCaptions(prev => prev.filter((_, i) => i !== index));
    setStagedCaptionsEdited(prev => prev.filter((_, i) => i !== index));
  }

  function stageAudio(blob: Blob, durationSec: number, label: string) {
    setStagedAudio(prev => [...prev, { blob, url: URL.createObjectURL(blob), durationSec, label }]);
  }

  function removeStagedAudio(index: number) {
    URL.revokeObjectURL(stagedAudio[index].url);
    setStagedAudio(prev => prev.filter((_, i) => i !== index));
  }

  function editStagedCaption(index: number, value: string) {
    setStagedCaptions(prev => prev.map((c, i) => i === index ? value : c));
    setStagedCaptionsEdited(prev => prev.map((e, i) => i === index ? true : e));
  }

  function toggleStagedTag(photoIdx: number, userId: string) {
    setStagedTags(prev =>
      prev.map((tags, i) =>
        i !== photoIdx ? tags :
        tags.includes(userId) ? tags.filter(u => u !== userId) : [...tags, userId]
      )
    );
  }

  // ── Edit-mode participant handlers (immediate API) ─────────────────────────

  function toggleParticipant(userId: string) {
    if (!id) return;
    const has = participants.includes(userId);
    if (has) {
      api.delete(`/events/${id}/participants/${userId}`)
        .then(() => setParticipants(prev => prev.filter(p => p !== userId)));
    } else {
      api.post(`/events/${id}/participants`, { userId })
        .then(() => setParticipants(prev => [...prev, userId]));
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (recorderBusy) {
      setError('Save or discard your voice note before creating the event.');
      return;
    }
    setError('');
    setLoading(true);
    const payload = { title, description, eventDate: eventDate || null, location, templateKind };

    let eventId = id;
    try {
      if (isEdit && id) {
        await api.put(`/events/${id}`, payload);
      } else {
        const { data } = await api.post('/events', payload);
        eventId = data.id;
      }
    } catch (err: unknown) {
      // Nothing was saved — safe to stay on the form and let the user retry.
      setError(serverErrorMessage(err) ?? 'Save failed');
      setLoading(false);
      return;
    }

    if (!isEdit) {
      // From here on the event EXISTS. Every step is best-effort and the user
      // always lands on the new event: showing "Save failed" and keeping the
      // form filled in made users resubmit, which created a duplicate event
      // each time — still carrying the same staged photo into the "next" one.
      const notes: string[] = [];

      const photoFailures: (string | null)[] = [];
      const allTaggedUserIds = new Set<string>();
      for (let i = 0; i < stagedPhotos.length; i++) {
        let photoId: string;
        try {
          const form = new FormData();
          form.append('photo', stagedPhotos[i]);
          if (stagedCaptions[i]) form.append('caption', stagedCaptions[i]);
          const { data: photo } = await api.post(`/events/${eventId}/photos`, form);
          photoId = photo.id;
        } catch (err) {
          photoFailures.push(serverErrorMessage(err));
          continue;
        }
        for (const userId of stagedTags[i] ?? []) {
          try {
            await api.post(`/events/${eventId}/photos/${photoId}/participants`, { userId });
            allTaggedUserIds.add(userId);
          } catch { /* a missed tag can be redone on the event page */ }
        }
      }
      if (photoFailures.length > 0) {
        notes.push(`${describeUploadFailures(photoFailures, stagedPhotos.length)} You can add photos from the event page.`);
      }

      const allParticipantIds = new Set([...allTaggedUserIds, ...stagedParticipants]);
      for (const userId of allParticipantIds) {
        try {
          await api.post(`/events/${eventId}/participants`, { userId });
        } catch { /* same — fixable from the event page */ }
      }

      let audioFailures = 0;
      for (const clip of stagedAudio) {
        try {
          const form = new FormData();
          form.append('audio', clip.blob, audioFileName(clip.blob));
          if (clip.label) form.append('label', clip.label);
          form.append('durationSec', String(clip.durationSec));
          await api.post(`/audio/events/${eventId}`, form);
        } catch {
          audioFailures++;
        }
      }
      if (audioFailures > 0) {
        notes.push(`${audioFailures === 1 ? 'A voice note' : `${audioFailures} voice notes`} couldn't be uploaded. You can record again from the event page.`);
      }

      if (notes.length > 0) alert(`Your event was created. ${notes.join(' ')}`);
    }

    navigate(`/events/${eventId}`);
  }

  const activeMembers = members.filter(m => m.status === 'active' || m.status === 'no_email' || m.status === 'pending');
  const humanMembers = activeMembers.filter(m => !m.petType);
  const petMembers = activeMembers.filter(m => m.petType);
  // Anyone already tagged in a staged photo is already going to be added as a
  // participant at submit time — don't ask the user to pick them again here.
  const taggedInPhotoIds = new Set(stagedTags.flat());
  const untaggedMembers = activeMembers.filter(m => !taggedInPhotoIds.has(m.user.id));

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6">
        {isEdit ? 'Edit Event' : 'New Event'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-2xl p-6 shadow-sm border border-indigo-50">

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">What kind of event is this?</label>
          <TemplatePicker value={templateKind} onChange={setTemplateKind} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Event name *</label>
          <input
            type="text" value={title} onChange={e => setTitle(e.target.value)}
            required
            placeholder={getEventTemplate(templateKind).titlePlaceholder}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* ── Photos (create mode) ── */}
        {!isEdit && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">Photos</label>
              {detectingFaces && (
                <span className="flex items-center gap-1.5 text-xs text-violet-600 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                  Detecting faces…
                </span>
              )}
            </div>

            {stagedPreviews.length > 0 && (
              <div className="space-y-3 mb-3">
                {stagedPreviews.map((src, i) => {
                  const faces = stagedFaces[i] ?? [];
                  const faceAssignedIds = new Set(faces.map(f => f.assignedUserId).filter(Boolean) as string[]);
                  const undetectedHumans = humanMembers.filter(m => !faceAssignedIds.has(m.user.id));
                  const hasMembersToShow = undetectedHumans.length > 0 || petMembers.length > 0;

                  return (
                    <div key={src} className="rounded-xl border border-indigo-100 bg-white">
                      <div className="flex gap-3 p-3">
                        {/* Photo thumbnail */}
                        <div className="relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-slate-100 group">
                          <img
                            src={src}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = UNSUPPORTED_PREVIEW_DATA_URL; }}
                          />
                          <button
                            type="button"
                            onClick={() => removeStagedPhoto(i)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>

                        <div className="flex-1 min-w-0 space-y-2.5">
                          <FaceTagRow
                            faces={faces}
                            members={humanMembers}
                            onAssign={(faceIdx, userId) => assignFace(i, faceIdx, userId)}
                            onAddNewPerson={(faceIdx, name) => handleAddPersonForFace(i, faceIdx, name)}
                          />

                          {/* Manual tagging for undetected members + pets */}
                          {hasMembersToShow && (
                            <div>
                              <p className="text-xs font-medium text-slate-500 mb-1.5">
                                {faces.length > 0 ? 'Anyone else in this photo?' : 'Who\'s in this photo?'}{' '}
                                <span className="font-normal text-slate-400">(optional)</span>
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {undetectedHumans.map(m => {
                                  const tagged = stagedTags[i]?.includes(m.user.id);
                                  return (
                                    <button
                                      key={m.user.id}
                                      type="button"
                                      onClick={() => toggleStagedTag(i, m.user.id)}
                                      className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${
                                        tagged
                                          ? 'bg-indigo-600 text-white border-indigo-600'
                                          : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                                      }`}
                                    >
                                      {tagged && <span className="mr-1">✓</span>}
                                      {(m.user.name ?? '?').split(' ')[0]}
                                    </button>
                                  );
                                })}
                                {petMembers.map(m => {
                                  const tagged = stagedTags[i]?.includes(m.user.id);
                                  return (
                                    <button
                                      key={m.user.id}
                                      type="button"
                                      onClick={() => toggleStagedTag(i, m.user.id)}
                                      className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${
                                        tagged
                                          ? 'bg-amber-500 text-white border-amber-500'
                                          : 'bg-white text-slate-500 border-slate-200 hover:border-amber-300 hover:text-amber-600'
                                      }`}
                                    >
                                      {tagged && <span className="mr-1">✓</span>}
                                      🐾 {(m.user.name ?? '?').split(' ')[0]}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                              <span aria-hidden="true">✨</span> Suggested caption
                            </p>
                            <input
                              type="text"
                              value={stagedCaptions[i] ?? ''}
                              onChange={e => editStagedCaption(i, e.target.value)}
                              placeholder="Add a caption…"
                              className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="flex items-center gap-2 text-sm text-indigo-600 border border-dashed border-indigo-300 rounded-lg px-4 py-2 hover:bg-indigo-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Photos
            </button>
            <input ref={photoInputRef} type="file" accept={PHOTO_ACCEPT} multiple className="hidden" onChange={handlePhotoSelect} />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
            <input
              type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
            <input
              type="text" value={location} onChange={e => setLocation(e.target.value)}
              placeholder="City, Country"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={description} onChange={e => setDescription(e.target.value)}
            rows={3} placeholder={`Tell the story of this ${getEventTemplate(templateKind).noun}…`}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        {/* ── Voice note (create mode) ── */}
        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Voice note <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Tell the story in your own voice — it plays alongside the photos.
            </p>
            {stagedAudio.length > 0 && (
              <div className="space-y-2 mb-2">
                {stagedAudio.map((clip, i) => (
                  <div key={clip.url} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2">
                    <div className="flex-1 min-w-0">
                      {clip.label && <p className="text-xs font-medium text-slate-700 truncate mb-1">{clip.label}</p>}
                      <audio src={clip.url} controls className="w-full h-8" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStagedAudio(i)}
                      className="text-xs text-slate-500 hover:text-red-600 px-2 py-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <AudioRecorder
              onSave={stageAudio}
              onBusyChange={setRecorderBusy}
              idleLabel={stagedAudio.length > 0 ? 'Record another' : 'Record a voice note'}
            />
          </div>
        )}

        {/* ── People at this event (create mode) ── */}
        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {stagedPhotos.length > 0 ? 'Anyone else who was there?' : 'People at this event'}
            </label>

            {membersError && (
              <p className="text-xs text-amber-600 mb-2">Could not load your circle — check your connection.</p>
            )}

            {untaggedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {untaggedMembers.map(m => {
                  const selected = stagedParticipants.includes(m.user.id);
                  return (
                    <button
                      key={m.user.id}
                      type="button"
                      onClick={() => setStagedParticipants(prev =>
                        selected ? prev.filter(uid => uid !== m.user.id) : [...prev, m.user.id]
                      )}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        selected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {selected && <span>✓</span>}
                      {m.petType ? '🐾 ' : ''}{m.user.name}
                      {m.relationship && <span className="opacity-70 text-xs">· {m.relationship}</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {!showAddMember ? (
              <button
                type="button"
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-1.5 text-sm text-indigo-500 hover:text-indigo-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add a new person
              </button>
            ) : (
              <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100 space-y-2 mt-1">
                <p className="text-xs font-semibold text-slate-600">New person</p>
                <div className="flex gap-2">
                  <input
                    type="text" value={newMemberName} onChange={e => setNewMemberName(e.target.value)}
                    placeholder="Name *"
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text" value={newMemberRelationship} onChange={e => setNewMemberRelationship(e.target.value)}
                    placeholder="Relationship (e.g. Mom)"
                    autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                {addMemberError && <p className="text-red-600 text-xs">{addMemberError}</p>}
                <div className="flex gap-2">
                  <button
                    type="button" onClick={handleAddMember}
                    disabled={!newMemberName.trim() || addMemberLoading}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {addMemberLoading ? 'Adding…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddMember(false); setNewMemberName(''); setNewMemberRelationship(''); setAddMemberError(''); }}
                    className="px-3 py-1.5 bg-white text-slate-600 rounded-lg text-sm hover:bg-slate-100 transition-colors border border-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Event-level participants (edit mode) ── */}
        {isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">People at this event</label>

            {membersError && (
              <p className="text-xs text-amber-600 mb-2">Could not load your circle — check your connection.</p>
            )}

            {activeMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {activeMembers.map(m => (
                  <button
                    key={m.id} type="button"
                    onClick={() => toggleParticipant(m.user.id)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-all ${
                      participants.includes(m.user.id) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {participants.includes(m.user.id) && <span>✓</span>}
                    {m.petType ? '🐾 ' : ''}{m.user.name}
                    {m.relationship && <span className="opacity-70 text-xs">· {m.relationship}</span>}
                  </button>
                ))}
              </div>
            )}

            {!showAddMember ? (
              <button
                type="button" onClick={() => setShowAddMember(true)}
                className="flex items-center gap-1.5 text-sm text-indigo-500 hover:text-indigo-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add a new person
              </button>
            ) : (
              <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100 space-y-2 mt-1">
                <p className="text-xs font-semibold text-slate-600">New person</p>
                <div className="flex gap-2">
                  <input
                    type="text" value={newMemberName} onChange={e => setNewMemberName(e.target.value)}
                    placeholder="Name *"
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text" value={newMemberRelationship} onChange={e => setNewMemberRelationship(e.target.value)}
                    placeholder="Relationship (e.g. Mom)"
                    autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                {addMemberError && <p className="text-red-600 text-xs">{addMemberError}</p>}
                <div className="flex gap-2">
                  <button
                    type="button" onClick={handleAddMember}
                    disabled={!newMemberName.trim() || addMemberLoading}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {addMemberLoading ? 'Adding…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddMember(false); setNewMemberName(''); setNewMemberRelationship(''); setAddMemberError(''); }}
                    className="px-3 py-1.5 bg-white text-slate-600 rounded-lg text-sm hover:bg-slate-100 transition-colors border border-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex gap-3 pt-1">
          <button
            type="submit" disabled={loading}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Event'}
          </button>
          <button
            type="button" onClick={() => navigate(-1)}
            className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
